import Datasource, { DatasourceResponse } from "lib/datasource";
import { isFeatureToggled } from "lib";
import _ from "lodash";
import pluralize from "pluralize";
import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { UserContext } from "context";
import { setSmidgeUser } from "./utils/session";
import axios from "./utils/axios";

export const useParams = (callback: (params: any) => any, dependency: any[]) => {
	const updateParams = useCallback(callback, dependency);
	const [params, setParams] = useState<any>({});
	useEffect(() => setParams(updateParams), [updateParams]);
	return params;
};

export const useGetEntity = (
	name: string,
	id?: string,
	queryStringParameters?: any,
	process: (nomalized: any) => any = entity => entity
) => {
	const [, renderTrigger] = useState(new Date());
	const [datasource] = useState(new Datasource({ mainModelName: name, renderTrigger: renderTrigger }));
	const [entity, setEntity] = useState(null);
	useEffect(() => {
		if (!id) return;
		const sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			const res = !!process ? process(response.normalizedMainModelResponse) : response.normalizedMainModelResponse;
			setEntity(res);
		});
		return () => sub.unsubscribe();
	}, [datasource, id, process]);

	useEffect(() => {
		if (_.every(queryStringParameters, v => !!v) && !_.isEmpty(queryStringParameters) && !!id)
			datasource.get("v2", `${pluralize(name!)}/${id}`, {
				queryStringParameters
			});
	}, [datasource, id, name, queryStringParameters]);
	return [entity, setEntity, datasource];
};

export const useGetEntities = (
	name: string,
	queryStringParameters?: any,
	process: (nomalized: any) => any = entities => entities
): any => {
	const [, renderTrigger] = useState(new Date());
	const [datasource] = useState(new Datasource({ mainModelName: name, renderTrigger: renderTrigger }));
	const [entities, setEntities] = useState(null);
	useEffect(() => {
		const sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			setEntities(process ? process(response.normalizedMainModelResponse) : response.normalizedMainModelResponse);
		});
		return () => sub.unsubscribe();
	}, [datasource, process]);

	useEffect(() => {
		if (_.every(queryStringParameters, v => !!v) && !_.isEmpty(queryStringParameters))
			datasource.get("v2", `${pluralize(name)}`, {
				queryStringParameters
			});
	}, [datasource, name, queryStringParameters]);

	return entities;
};

export const useGetTenancy = (tenancy_id?: string, queryParameters?: any) => {
	const tenancyParams = useParams(
		params => ({
			...params,
			...queryParameters
		}),
		[]
	);
	const [tenancy, setTenancy, datasource] = useGetEntity("tenancy", tenancy_id, tenancyParams);
	return { tenancy, datasource, setTenancy } as { tenancy: any; datasource: any; setTenancy: any };
};

export const useGetLease = (lease_id: string, queryParameters?: any) => {
	const leaseParams = useParams(
		params => ({
			...params,
			...queryParameters
		}),
		[]
	);
	const [lease, setLease, datasource] = useGetEntity("lease", lease_id, leaseParams);
	return { lease, datasource, setLease } as { lease: any; datasource: any; setLease: any };
};

// TODO: get single building should depend on entity show API endpoint rather than the index endpoint.
export const useGetBuilding = (property_id: string, building_id: string, name: string = "building") => {
	const buildingsParams = useParams(params => ({ ...params, property_id, per_page: 100 }), [property_id]);
	const building = useGetEntities(name, buildingsParams, buildings => _.find(buildings, { id: building_id }));
	return building;
};

export const useGetBuildings = (property_id?: string, name: string = "building") => {
	const buildingsParams = useParams(params => ({ ...params, property_id, per_page: 100 }), [property_id]);
	const buildings = useGetEntities(name, buildingsParams);
	return buildings;
};

export const useGetBuildingsWithPrecinct = (building_id: string, queryParameters?: any) => {
	const buildingsParams = useParams(
		params => ({ 
			...params, 
			...queryParameters
		}),
		[]
	);
	const [building, setBuilding, datasource] = useGetEntity("building", building_id, buildingsParams);
	return { building, datasource, setBuilding } as { building: any; datasource: any; setBuilding: any };
};

export const useGetAddresses = (property_id?: string, name: string = "address") => {
	const addressesParams = useParams(params => ({ ...params, property_id, per_page: 100 }), [property_id]);
	const addresses = useGetEntities(name, addressesParams);
	return addresses;
};

/**
 * How many pending projects the current user OWNS.
 *
 * Reads `totalCount` off the pagination meta with `per_page: 1` rather than
 * counting rows — the list itself is never needed here, only its size.
 *
 * Owner-scoped since 17 Sep 2026, in the BACKEND (`Project.pending_for`), not
 * here: the Pending Projects page, the count under the Projects nav icon and
 * the "Pending Projects" row on the dashboard all resolve to that one scope, so
 * a badge can never disagree with the list it links to.
 *
 * `enabled` exists because the Masthead is also rendered by App.tsx's Suspense
 * fallback, which paints before the session is known. Firing this there would
 * mean a guaranteed 401 on every cold load; the failure is already swallowed
 * into 0, but the request is still worth not making.
 *
 * `refreshKey` re-reads the count whenever it changes. A caller that lives for
 * the whole session — the Masthead, which mounts once and never unmounts —
 * would otherwise hold a mount-time number forever: finish the scheduling
 * wizard and the badge keeps the old count until a hard refresh, contradicting
 * the dashboard on the same screen. It passes the current pathname.
 */
export const usePendingProjectsCount = (enabled: boolean = true, refreshKey?: string | number) => {
	const [count, setCount] = useState(0);
	const [datasource] = useState(new Datasource({ mainModelName: "project", perPage: 1, currentPage: 1 }));

	useEffect(() => {
		if (!enabled) return;
		const sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setCount(response.success ? _.get(response, "datasourceMeta.totalCount", 0) : 0)
		);
		datasource.get("v2", "projects/pending", { queryStringParameters: { per_page: 1, page: 1 } });
		return () => sub.unsubscribe();
	}, [datasource, enabled, refreshKey]);

	return count;
};

/**
 * The number beside "ToDos" in the Masthead: OPEN ToDos ASSIGNED TO the current
 * user (Sim, 24 Sep 2026) — `GET /v2/todos-feed/open-count`. Ones they created
 * for someone else are not counted. Same `enabled` / `refreshKey` contract as
 * usePendingProjectsCount: the Masthead passes the pathname, so closing a ToDo
 * and moving on updates the number without a reload. Any failure reads as 0.
 */
export const useOpenTodosCount = (enabled: boolean = true, refreshKey?: string | number) => {
	const [count, setCount] = useState(0);

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		axios
			.get(`${process.env.REACT_APP_API_ENDPOINT_V2}todos-feed/open-count`)
			.then((res: any) => {
				if (!cancelled) setCount(Number(_.get(res, "data.count", 0)) || 0);
			})
			.catch(() => {
				if (!cancelled) setCount(0);
			});
		return () => {
			cancelled = true;
		};
	}, [enabled, refreshKey]);

	return count;
};

export const useAddClassNames = (selector: string, styles: string[]) => {
	useEffect(() => {
		const element = document.querySelector(selector);
		for (let style of styles) {
			element?.classList.add(style);
		}

		return () => {
			for (let style of styles) {
				element?.classList.remove(style);
			}
		};
	}, [selector, styles]);
};

export const useOverrideStyles = (selector: string, styles: { [key: string]: string }) => {
	useEffect(() => {
		const elements = document.querySelectorAll(selector);
		for (let e of Array.from(elements)) {
			for (let key in styles) {
				(e as any).style[key] = styles[key];
			}
		}
		return () => {
			for (let e of Array.from(elements)) {
				for (let key in styles) {
					(e as any).style[key] = styles[key];
				}
			}
		};
	}, [selector, styles]);
};

export const useCurrentUserForSmidge = () => {
	const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
	useEffect(() => {
		if (!_.isEmpty(currentUser))
			setSmidgeUser({
				id: currentUser.id,
				email: currentUser.email,
				name: `${currentUser.firstname} ${currentUser.surname}`
			});
	}, [currentUser]);
};

/**
 * A SCREEN BEHIND A FEATURE FLAG. Returns whether the current user may be here,
 * and sends them to the dashboard when they may not.
 *
 * Hiding a masthead entry is not access control: the URL is still typable, an
 * old bookmark still resolves, and a link in an email still lands. Without this
 * the screen mounted anyway and showed whatever its API 403 turned into — for
 * `/lease-agreements/:id` that was the page shell plus "This lease is not
 * available", which reads like missing data rather than "not yours" (Sim,
 * 22 Sep 2026).
 *
 * USE BOTH HALVES:
 *
 *   const allowed = useFeatureFlagGuard("lease-agreements");
 *   if (!allowed) return null;
 *
 * The redirect is an effect, and effects run AFTER the render that scheduled
 * them — without the early return the screen paints for one frame first. Same
 * two-part shape as the pending-project guard in `Projects/Create.tsx`.
 *
 * `replace`, not `push`, so Back off the dashboard does not bounce straight
 * back into the screen they were just turned away from.
 *
 * This guards the UI only. The API keeps its own gate (`authorize_feature_flag!`
 * on the matching controller) — that is the enforcement, and it stays the
 * authority for anything calling the endpoints directly.
 */
export const useFeatureFlagGuard = (flag: string, redirectTo: string = "/") => {
	const history = useHistory();
	const { currentUser } = useContext(UserContext) as { currentUser: tcpinpoint.User };
	const allowed = !!isFeatureToggled(flag, currentUser);

	useEffect(() => {
		if (!allowed) history.replace(redirectTo);
	}, [allowed, history, redirectTo]);

	return allowed;
};
